import { HttpStatusCode } from 'axios';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Entity, User } from '../types/apiTypes';
import { useAuth } from './AuthContext';
import useApi from './AxiosWrapper';
import { tracker } from '../App';
import toast from 'react-hot-toast';
import { ETID_STORAGE_KEY, clearEtidCookie } from '../shared/etid';
import { clearLandingCookie } from '../shared/landing';
import { PARTNER_CODE_STORAGE_KEY } from '../shared/partnerCode';
import {
  SELF_REPORTED_EMAIL_STORAGE_KEY,
  clearSelfReportedEmailCookie,
} from '../shared/selfReportedEmail';
import { OAUTH_STORAGE_KEYS } from './OAuthHandler';
import { HEADS_UP_STORAGE_PREFIX } from '../constants/headsUp';
import { PLAID_HANDOFF_KEY } from './onboardingHandoff';
import { delay } from './Delay';
import {
  safeGetItem,
  safeKeys,
  safeRemoveItem,
  safeSessionGetItem,
  safeSessionKeys,
  safeSessionRemoveItem,
  safeSetItem,
} from '../shared/safeStorage';

// Signup-flow drafts cached in sessionStorage. Several contain PII in
// plaintext JSON (SSN/DOB in personalInfo, EIN in businessInfo) and must not
// outlive the user's session — auth0Logout is a same-tab redirect, so
// sessionStorage survives it.
const SIGNUP_STORAGE_KEYS = [
  'personalInfo',
  'businessInfo',
  // Both agreement keys are legacy — nothing writes them now that the two steps were merged, and
  // the combined step persists nothing. They stay listed to clear whatever is already sitting in
  // a customer's tab: 'masterServiceAgreement' held a typed legal name and was never swept, so on
  // a shared machine it could be restored into the next person's signature field.
  'masterServiceAgreement',
  'bankAgreement',
  'documentUploadInfo',
  'managementContactInfo',
  'promotionInfo',
  'carrierNumberError',
  'vendorPersonalInfo',
  'vendorBusinessInfo',
  'vendorManagementContact',
  'partnerPersonalInfo',
  'partnerBusinessInfo',
  'partnerContract',
  'driver_invite_id',
  'partner_agent_invite_id',
];

// Keys written during signup/login and OAuth integration flows
// (see index.tsx, AuthRouter.tsx, OAuthHandler.tsx, IntegrationSettings.tsx)
// that must not survive logout.
const LOGOUT_STORAGE_KEYS = [
  'tachUserRole',
  'tachref',
  'tachRole',
  'redirectPath',
  ETID_STORAGE_KEY,
  PARTNER_CODE_STORAGE_KEY,
  SELF_REPORTED_EMAIL_STORAGE_KEY,
  ...OAUTH_STORAGE_KEYS,
  ...SIGNUP_STORAGE_KEYS,
  // Not a signup draft, so it doesn't belong in the array above — a one-shot instruction to the
  // dashboard, normally spent the moment it loads. Swept for the customer who signs out between
  // finishing the application and arriving, so it can't open Plaid on the next login.
  PLAID_HANDOFF_KEY,
];

// Result of looking up the authenticated user's account record.
// - 'unknown': not looked up yet
// - 'found':   record exists (userRecord is set)
// - 'absent':  lookup definitively returned no account (new user → onboarding)
// - 'error':   lookup failed transiently (token/network) — NOT proof of "no
//              account", so consumers must not treat it as a new user
export type UserFetchOutcome = 'unknown' | 'found' | 'absent' | 'error';

interface UserContextProps {
  userRecord: User | null;
  setUserRecord: (user: User | null) => void;
  userRole: Entity | null;
  setUserRole: (role: Entity | null) => void;
  userId: string | undefined;
  userType: Entity | undefined;
  logout: () => void;
  refetchUser: () => Promise<void>;
  isLoadingUser: boolean;
  isLoggingOut: boolean;
  userFetchOutcome: UserFetchOutcome;
}

export const UserContext = createContext<UserContextProps | undefined>(
  undefined
);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userRecord, setUserRecord] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<Entity | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const {
    user,
    isAuthenticated,
    isLoadingCached,
    logout: auth0Logout,
  } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // True once we've finished at least one user-fetch attempt for the current
  // authenticated session. Until then, an authenticated user with userRecord
  // still null is *resolving*, not record-less — see isResolvingUser below.
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState<boolean>(false);
  const [userFetchOutcome, setUserFetchOutcome] =
    useState<UserFetchOutcome>('unknown');
  const { callProtectedApi } = useApi();
  const loginAttemptInProgress = useRef(false);

  const getUserId = React.useCallback((): string | undefined => {
    if (!userRecord) return undefined;

    switch (userRecord.userType) {
      case 'customer':
        return userRecord.customer_id;
      case 'vendor':
        return userRecord.vendor_id;
      case 'partner':
        return userRecord.partner_id;
      case 'driver':
        return userRecord.driver_id;
      case 'partnerAgent':
        return userRecord.partner_agent_id;
      default:
        return undefined;
    }
  }, [userRecord]);

  const tryCreateOrFetchUser = async (role: Entity, user: any) => {
    try {
      let roleUsed = role.toString();
      if (role === Entity.Driver) {
        roleUsed = 'driverInvites';
      } else if (role === Entity.PartnerAgent) {
        roleUsed = 'partnerAgentInvites';
      } else {
        roleUsed = role.toLowerCase() + 's';
      }
      const endpoint = `/${roleUsed}`;
      const response = await callProtectedApi(
        endpoint,
        role !== Entity.Driver && role !== Entity.PartnerAgent ? 'post' : 'get',
        {
          email: user?.email,
          sub_id: user?.sub,
          email_verified: user?.email_verified,
          phone_number: user?.phone_number,
        },
        `Error occurred while creating ${role} account.`
      );

      if (
        response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created
      ) {
        const data = response?.data?.data?.[role];
        if (data && Object.entries(data)?.length > 0) {
          const userWithType = {
            ...data,
            userType: role as Entity,
          };
          setUserRecord(userWithType);
          setUserRole(role);
          safeSetItem('tachUserRole', role);
          return true;
        }
      } else if (response?.status === HttpStatusCode.Conflict) {
        toast.error(
          'This TACH login is associated with an existing user record.'
        );
        setUserRecord(null);
        setUserRole(null);
        safeRemoveItem('tachUserRole');
      }
      return false;
    } catch (e: any) {
      console.error(`Error with ${role} login:`, e?.message);
      return false;
    }
  };

  const tryFindUser = async (): Promise<UserFetchOutcome> => {
    try {
      const response = await callProtectedApi('/auth', 'post', {});

      if (response?.status === HttpStatusCode.Ok) {
        const data = response?.data?.data?.user;
        if (data && Object.entries(data)?.length > 0) {
          const userWithType = {
            ...data,
          };
          setUserRecord(userWithType);
          setUserRole(data?.userType as Entity);
          safeSetItem('tachUserRole', data?.userType as Entity);
          return 'found';
        }
        // 200 with an empty body means the account genuinely doesn't exist yet.
        return 'absent';
      }

      // A 404 is also a definitive "no account". Anything else (401/403/500, or
      // no response at all because the access token wasn't ready / network
      // blip) is a TRANSIENT failure — not proof the user has no account.
      if (response?.status === HttpStatusCode.NotFound) {
        return 'absent';
      }
      return 'error';
    } catch (e: any) {
      console.error(`Error with finding user account:`, e?.message);
      return 'error';
    }
  };

  const attemptLogin = async (user: any) => {
    // Prevent duplicate login attempts
    if (loginAttemptInProgress.current) {
      return;
    }

    loginAttemptInProgress.current = true;
    // Don't set isLoading here as it should already be set by the caller
    try {
      // ALWAYS try to find existing user first. Retry on transient failures
      // (e.g. the access token isn't minted yet immediately after the Auth0
      // callback) so an existing active/pre_active user is never mistaken for a
      // new one and wrongly sent to /role.
      let outcome = await tryFindUser();
      for (let attempt = 0; outcome === 'error' && attempt < 3; attempt++) {
        await delay(600 * (attempt + 1));
        outcome = await tryFindUser();
      }

      if (outcome === 'found') {
        // User exists - we've set the correct role from the database
        setUserFetchOutcome('found');
        setIsLoading(false);
        return;
      }

      if (outcome === 'absent') {
        // No account row exists yet. Only auto-create/fetch in a genuine,
        // invite-backed onboarding context — NEVER resurrect a role from a
        // leftover localStorage 'tachUserRole'. Doing so previously created a
        // shadow Customer for an invited driver during the no-role window,
        // permanently masking the driver (THT-653). Customer/vendor/partner
        // accounts are created explicitly in RoleSelect, so falling through to
        // /role here is the correct, safe behavior for them.
        const savedRole = safeGetItem('tachUserRole') as Entity;
        const driverInviteId = safeSessionGetItem('driver_invite_id');
        const partnerAgentInviteId = safeSessionGetItem(
          'partner_agent_invite_id'
        );
        const inviteBackedRole =
          (savedRole === Entity.Driver && !!driverInviteId) ||
          (savedRole === Entity.PartnerAgent && !!partnerAgentInviteId);

        if (inviteBackedRole) {
          const success = await tryCreateOrFetchUser(savedRole, user);
          if (success) {
            setUserFetchOutcome('found');
            setIsLoading(false);
            return;
          }
        }

        setUserRole(null);
        setUserRecord(null);
        setUserFetchOutcome('absent');
        setIsLoading(false);
        return;
      }

      // Still failing after retries — keep any existing record and mark the
      // outcome as error so AuthRouter waits instead of dumping the user to
      // /role on what is most likely a temporary auth/network problem.
      setUserFetchOutcome('error');
      setIsLoading(false);
    } catch (error) {
      console.error('Error in attemptLogin:', error);
      setUserFetchOutcome('error');
      setIsLoading(false);
    } finally {
      loginAttemptInProgress.current = false;
      setHasAttemptedFetch(true);
    }
  };

  // isLoggingOut (set synchronously here) both gates the fetch effect below so
  // it can't revive the session mid-logout, and drives the loader while the
  // redirect settles. The function is async so callers stay fire-and-forget
  // (the () => void contract is unchanged) while we await the redirect.
  const logout = async () => {
    setIsLoggingOut(true);
    setUserRole(null);
    setUserRecord(null);
    setHasAttemptedFetch(false);
    setUserFetchOutcome('unknown');
    LOGOUT_STORAGE_KEYS.forEach((key) => {
      safeRemoveItem(key);
      safeSessionRemoveItem(key);
    });
    // The heads-up page bakes the customer id into its keys, so they cannot be listed above —
    // sweep the namespace instead. They hold the MC/DOT numbers the customer typed and the
    // "customer service has been told" flag, neither of which may greet the next login.
    safeSessionKeys()
      .filter((key) => key.startsWith(HEADS_UP_STORAGE_PREFIX))
      .forEach((key) => safeSessionRemoveItem(key));
    clearEtidCookie();
    clearSelfReportedEmailCookie();
    clearLandingCookie();

    const returnTo = window.location.origin + '/login';
    try {
      // auth0Logout (auth0-spa-js v2) awaits cacheManager.clear() before
      // window.location.assign(returnTo), so the redirect lands on a later
      // microtask — awaiting it keeps local-state teardown from racing the
      // navigation. On this happy path auth0Logout performs the navigation
      // itself; we deliberately do NOT navigate again here, or the user would
      // be redirected twice.
      await auth0Logout({
        logoutParams: {
          returnTo,
        },
      });
    } catch (error) {
      // Guaranteed fallback: if auth0Logout rejects/throws it may never have
      // navigated, so send the user to /login ourselves rather than strand them
      // in a half-torn-down session.
      console.error('Error during logout:', error);
      window.location.assign(returnTo);
    }
  };

  /**
   * Manually refetch the current user's record from the API
   * Useful when you expect the user data has been updated from another component
   */
  const refetchUser = async () => {
    try {
      // Only promote the outcome on a definitive hit. A refetch that returns
      // 'absent'/'error' (e.g. an eventual-consistency window right after
      // account creation, or a transient blip) must not overwrite a record we
      // already hold or flip the outcome to a misleading 'absent'.
      const outcome = await tryFindUser();
      if (outcome === 'found') {
        setUserFetchOutcome('found');
      }
    } catch (error) {
      console.error('Error refetching user:', error);
    }
  };

  // Set entity id as "pk" for OpenReplay
  useEffect(() => {
    if (userRecord) {
      const id = getUserId();
      if (id) tracker.setUserID(id);
    }
  }, [userRecord, getUserId]);

  // Fetch the user's record exactly once per mounted authenticated session.
  // Gating on hasAttemptedFetch (not userRecord === null) is critical: if the
  // lookup returns no record (error/absent), userRecord stays null, and gating
  // on it would re-fire this effect every time `user`'s identity churns —
  // spinning forever between "Fetching..." and the page's own loader.
  useEffect(() => {
    // !isLoggingOut is essential: logout() resets hasAttemptedFetch to false
    // while isAuthenticated is still true (the redirect hasn't navigated yet),
    // which would otherwise re-fire attemptLogin and revive the very session
    // logout is tearing down — the root cause of the "two clicks to log out"
    // bug (THT-694). Suppressing only while a logout is in flight leaves the
    // normal login/onboarding fetch path untouched.
    if (
      isAuthenticated &&
      !isLoadingCached &&
      !hasAttemptedFetch &&
      !isLoading &&
      !isLoggingOut
    ) {
      setIsLoading(true); // Set loading immediately to prevent race condition
      attemptLogin(user);
    }
    // Routing is now handled by AuthRouter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated, isLoadingCached, hasAttemptedFetch, isLoggingOut]);

  // An authenticated user whose record hasn't been fetched yet is still
  // resolving — not record-less. Without this, there's a one-render window
  // after auth where (userRecord === null && isLoading === false) before the
  // fetch effect runs, during which AuthRouter would wrongly send active/
  // pre_active users to /role. Gating on it keeps consumers from acting until
  // the fetch settles, so only genuinely record-less users reach /role.
  const isResolvingUser =
    isLoading || (isAuthenticated && !isLoadingCached && !hasAttemptedFetch);

  // The lookup failed transiently and never recovered after retries. Without an
  // explicit recovery state the app would render children that sit on a loader
  // forever (no record, nothing to route to). Offer a retry instead of hanging.
  const userLookupFailed =
    isAuthenticated && !userRecord && userFetchOutcome === 'error';

  return (
    <>
      {isResolvingUser || isLoggingOut ? (
        <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
          <img
            alt="loading-icon"
            src="/Icon-v2.gif"
            className="flex h-44 w-44 justify-center"
          />
          <h2 className="text-[14px] text-secondary">
            {isLoggingOut
              ? 'Logging out...'
              : 'Fetching your account record...'}
          </h2>
        </div>
      ) : userLookupFailed ? (
        <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center gap-4 align-middle">
          <h2 className="text-[16px] font-medium">
            We couldn&apos;t load your account
          </h2>
          <p className="text-[14px] text-secondary">
            Please check your connection and try again.
          </p>
          <button
            type="button"
            onClick={() => {
              // Drop any stale Auth0 cache so the reload starts a clean session
              // and re-authenticates, rather than re-reading a token that can't
              // be renewed (the usual cause of this screen in local dev).
              safeKeys()
                .filter((k) => k.startsWith('@@auth0spajs@@'))
                .forEach((k) => safeRemoveItem(k));
              window.location.reload();
            }}
            className="rounded-full bg-accent px-6 py-2 text-[14px] font-medium text-white"
          >
            Try again
          </button>
        </div>
      ) : (
        <UserContext.Provider
          value={{
            userRecord,
            setUserRecord,
            userRole,
            setUserRole,
            userId: getUserId(),
            userType: userRecord?.userType as Entity,
            logout,
            refetchUser,
            isLoadingUser: isResolvingUser,
            isLoggingOut,
            userFetchOutcome,
          }}
        >
          {children}
        </UserContext.Provider>
      )}
    </>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
